import type {
  AgencyRef,
  AgencyServedPortRef,
  ClientRef,
  PortCallTypeRef,
  SearchedVesselRef
} from '@gat-ship/types'
import type { StepOption } from '../components/providers/create-portcall-provider/step-flow/create-portcall-steps'

/**
 * Pure reference-data → {@link StepOption} mappers for the create-portcall
 * step flow's list steps, plus the client-side substring filter the
 * list-and-filter steps apply as the user types. Each mapper returns a fresh
 * array; none mutate their input. The composable (`usePortcallStepSources`)
 * owns the reactive sources and threads their values through these.
 */

/** Agencies → options, alpha-sorted by name (the only sorted list). */
export function toAgencyOptions(agencies: readonly AgencyRef[]): StepOption[] {
  return agencies
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map<StepOption>((a) => ({
      value: a.id,
      label: a.name,
      sub: a.shortName
    }))
}

/** Appointing-principal clients → options (server-search order kept). */
export function toClientOptions(clients: readonly ClientRef[]): StepOption[] {
  return clients.map<StepOption>((c) => ({
    value: c.id,
    label: c.name,
    sub: c.shortName ?? undefined
  }))
}

/** Vessels → options keyed by IMO, with the flag-state country code. */
export function toVesselOptions(
  vessels: readonly SearchedVesselRef[]
): StepOption[] {
  return vessels.map<StepOption>((v) => ({
    value: v.imoNumber,
    label: v.name,
    sub: [v.imoNumber, v.vesselTypeName].filter(Boolean).join(' · '),
    flag: v.flagCountryCode
  }))
}

/** Port-call types → options. */
export function toCallTypeOptions(
  types: readonly PortCallTypeRef[]
): StepOption[] {
  return types.map<StepOption>((t) => ({
    value: t.id,
    label: t.name
  }))
}

/** Agency-served ports → options, flagging the agency's default port. */
export function toPortOptions(
  ports: readonly AgencyServedPortRef[]
): StepOption[] {
  return ports.map<StepOption>((p) => ({
    value: p.locationCode,
    label: p.name,
    sub: [p.locationCode, p.isDefault ? 'Default' : undefined]
      .filter(Boolean)
      .join(' · ')
  }))
}

/**
 * Reconcile a selected port against an agency's served ports. Keeps the current
 * selection when the (new) agency still serves it; otherwise falls back to the
 * agency's default port, or `null` when there's neither. Drives the port-of-call
 * reset that runs whenever the chosen agency changes.
 */
export function reconcileSelectedPort(
  selectedCode: string,
  ports: readonly AgencyServedPortRef[]
): AgencyServedPortRef | null {
  if (selectedCode) {
    const current = ports.find((p) => p.locationCode === selectedCode)
    if (current) return current
  }
  return ports.find((p) => p.isDefault) ?? null
}

/** Case-insensitive substring filter over an option's label + sub. */
export function filterStepOptions(
  options: readonly StepOption[],
  query: string
): StepOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return options.slice()
  return options.filter((o) =>
    `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(q)
  )
}

/**
 * Merge locally-created principal options ahead of the server-search results,
 * de-duplicated by value. A client created inline via "Add client" won't be
 * returned by the debounced server search yet, so it's pinned here to keep the
 * just-created (and now selected) principal visible in the list.
 */
export function mergePrincipalOptions(
  created: readonly StepOption[],
  searched: readonly StepOption[]
): StepOption[] {
  if (created.length === 0) return searched.slice()
  const createdValues = new Set(created.map((o) => o.value))
  return [...created, ...searched.filter((o) => !createdValues.has(o.value))]
}
